import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { Download, FileText, Film, Image as ImageIcon } from "lucide-react";
import PreviewStrip from "./PreviewStrip";

function ExportRow({ icon, title, desc, onClick, loading, testId }) {
  return (
    <div className="border border-zinc-800 rounded-md p-4 flex items-center gap-4">
      <div className="text-blue-400">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold">{title}</div>
        <div className="text-xs text-zinc-400">{desc}</div>
      </div>
      <Button data-testid={testId} onClick={onClick} disabled={loading} className="bg-blue-600 hover:bg-blue-500 shrink-0">
        <Download className="w-4 h-4 mr-2" /> {loading ? "Working…" : "Download"}
      </Button>
    </div>
  );
}

export default function ExportDialog({
  open,
  onOpenChange,
  pdfPreset, setPdfPreset,
  customW, setCustomW,
  customH, setCustomH,
  slidesPerPage, setSlidesPerPage,
  marginPct, setMarginPct,
  exporting,
  exportMp4,
  exportImages,
  exportPdf,
  previewPages,
  previewMeta,
  previewLoading,
  fetchPreview,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50">
        <DialogHeader>
          <DialogTitle className="font-display">Export journal</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Download your slideshow as MP4, PDF, or a ZIP of 1920×1080 PNG slides.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <ExportRow
            icon={<Film className="w-5 h-5" />}
            title="MP4 Video"
            desc="A full slideshow video, tier durations applied. Plays everywhere."
            testId="export-mp4-btn"
            loading={exporting === "mp4"}
            onClick={exportMp4}
          />
          <ExportRow
            icon={<ImageIcon className="w-5 h-5" />}
            title="Bulk Images (ZIP)"
            desc="One 1920×1080 PNG per slide, perfect for Premiere."
            testId="export-images-btn"
            loading={exporting === "images"}
            onClick={exportImages}
          />
          <div className="border border-zinc-800 rounded-md p-4 space-y-3">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-blue-400" />
              <div>
                <div className="font-display font-semibold">PDF Journal</div>
                <div className="text-xs text-zinc-400">Pick a preset or use custom dimensions.</div>
              </div>
            </div>
            <Select value={pdfPreset} onValueChange={setPdfPreset}>
              <SelectTrigger data-testid="pdf-preset-select" className="bg-zinc-950 border-zinc-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                <SelectItem value="letter">Letter (8.5 × 11)</SelectItem>
                <SelectItem value="letter_landscape">Letter Landscape (11 × 8.5)</SelectItem>
                <SelectItem value="book_6x9">Book 6 × 9</SelectItem>
                <SelectItem value="digest_55x85">Digest 5.5 × 8.5</SelectItem>
                <SelectItem value="a4">A4 (8.27 × 11.69)</SelectItem>
                <SelectItem value="a4_landscape">A4 Landscape</SelectItem>
                <SelectItem value="square_8x8">Square 8 × 8</SelectItem>
                <SelectItem value="tabloid_11x17">Tabloid 11 × 17</SelectItem>
                <SelectItem value="slide_16x9">Slide 16 × 9</SelectItem>
                <SelectItem value="custom">Custom…</SelectItem>
              </SelectContent>
            </Select>
            {pdfPreset === "custom" && (
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="pdf-custom-w" type="number" step={0.1} value={customW} onChange={(e) => setCustomW(e.target.value)} className="bg-zinc-950 border-zinc-800" placeholder="Width (in)" />
                <Input data-testid="pdf-custom-h" type="number" step={0.1} value={customH} onChange={(e) => setCustomH(e.target.value)} className="bg-zinc-950 border-zinc-800" placeholder="Height (in)" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="label-eyebrow">Slides per page</Label>
                <Select value={String(slidesPerPage)} onValueChange={(v) => setSlidesPerPage(Number(v))}>
                  <SelectTrigger data-testid="pdf-spp-select" className="bg-zinc-950 border-zinc-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                    <SelectItem value="1">1 (full page)</SelectItem>
                    <SelectItem value="2">2 (stacked)</SelectItem>
                    <SelectItem value="4">4 (2 × 2 grid)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="label-eyebrow">Margin · {marginPct}%</Label>
                <input
                  data-testid="pdf-margin-slider"
                  type="range"
                  min={0}
                  max={15}
                  step={1}
                  value={marginPct}
                  onChange={(e) => setMarginPct(Number(e.target.value))}
                  className="w-full accent-blue-500"
                />
              </div>
            </div>
            <p className="text-xs text-zinc-500">
              Slides are resized & centered to fit the book page. Set per-journal backgrounds in the Inspector.
            </p>
            <Button data-testid="export-pdf-btn" onClick={exportPdf} disabled={exporting === "pdf"} className="w-full bg-blue-600 hover:bg-blue-500">
              <Download className="w-4 h-4 mr-2" /> {exporting === "pdf" ? "Exporting…" : "Download PDF"}
            </Button>

            <PreviewStrip
              previewPages={previewPages}
              previewMeta={previewMeta}
              previewLoading={previewLoading}
              fetchPreview={fetchPreview}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
